// src/services/fieldVisibilityService.js
import validationRulesService from './validationRulesService';

const fieldVisibilityService = {
  /**
   * Détermine quels champs doivent être visibles en fonction des règles de validation
   * @param {Object} formData - Les données du formulaire actuelles
   * @returns {Promise<Object>} - Un objet indiquant la visibilité des champs
   */
  getFieldVisibility: async (formData) => {
    try {
      // Récupérer toutes les règles de validation
      const rules = await validationRulesService.getAllRules();
      
      console.log("Règles récupérées pour la visibilité:", rules);
      console.log("Données du formulaire:", formData);
      
      // Objet pour stocker l'état de visibilité des champs
      const visibility = {
        datereclamationinitiale: false,
        modeintroductioninitiale: false,
        precisionagence: false,
        nomgestionnaire: false
      };
      
      // Pour chaque règle de type "Required", déterminer si elle rend un champ visible
      rules.forEach(rule => {
        if (rule.ruleType === "Required") {
          // Normaliser le nom du champ cible (tout en minuscules)
          const targetField = rule.targetField.toLowerCase();
          
          // Vérifier si les conditions de la règle sont satisfaites
          const isVisible = shouldShowField(rule, formData);
          
          console.log(`Règle ${rule.id} - ${rule.name}: Visibilité ${isVisible}`);
          
          // Mettre à jour la visibilité si les conditions sont remplies
          if (isVisible) {
            // Pour les champs spécifiques que nous voulons contrôler
            switch (targetField) {
              case 'datereclamationinitiale':
                visibility.datereclamationinitiale = true;
                break;
              case 'modeintroductioninitiale':
                visibility.modeintroductioninitiale = true;
                break;
              case 'precisionagence':
                visibility.precisionagence = true;
                break;
              case 'nomgestionnaire':
                visibility.nomgestionnaire = true;
                break;
            }
          }
        }
      });
      
      console.log("Visibilité finale des champs:", visibility);
      return visibility;
    } catch (error) {
      console.error("Erreur lors de la détermination de la visibilité des champs:", error);
      
      // En cas d'erreur, retourner la visibilité par défaut basée sur formData
      // Cela garantit que le formulaire reste fonctionnel même si l'API est indisponible
      const isRelance = formData.isRelance === 'oui';
      const modeIntro = formData.modeIntroductionInitiale || '';
      
      return {
        datereclamationinitiale: isRelance,
        modeintroductioninitiale: isRelance,
        precisionagence: isRelance && modeIntro === 'agence',
        nomgestionnaire: isRelance && modeIntro === 'gestionnaire'
      };
    }
  }
};

/**
 * Détermine si un champ doit être affiché selon ses conditions
 * @param {Object} rule - La règle de validation
 * @param {Object} formData - Les données du formulaire
 * @returns {Boolean} - True si le champ doit être affiché
 */
function shouldShowField(rule, formData) {
  // Si pas de conditions, le champ n'est pas conditionnel
  if (!rule.conditions || rule.conditions.length === 0) {
    return false;
  }
  
  let result = true;
  let isFirst = true;
  
  // Évaluer chaque condition
  for (const condition of rule.conditions) {
    // Normaliser le nom du champ source (prendre en compte la casse)
    const sourceField = condition.sourceField.charAt(0).toLowerCase() + 
                        condition.sourceField.slice(1);
    
    // Récupérer la valeur du champ
    const fieldValue = formData[sourceField];
    
    console.log(`Évaluation de la condition: ${sourceField} ${condition.operator} ${condition.value}`);
    console.log(`Valeur actuelle: ${fieldValue}`);
    
    // Évaluer la condition
    const conditionResult = evaluateCondition(condition, fieldValue);
    
    // Pour la première condition, initialiser le résultat
    if (isFirst) {
      result = conditionResult;
      isFirst = false;
    } else {
      // Pour les conditions suivantes, appliquer l'opérateur logique
      const logicalOp = condition.logicalOperator?.toUpperCase() || 'AND';
      if (logicalOp === 'AND') {
        result = result && conditionResult;
      } else if (logicalOp === 'OR') {
        result = result || conditionResult;
      }
    }
  }
  
  return result;
}

/**
 * Évalue une condition spécifique
 * @param {Object} condition - La condition à évaluer
 * @param {*} fieldValue - La valeur du champ à comparer
 * @returns {Boolean} - True si la condition est satisfaite
 */
function evaluateCondition(condition, fieldValue) {
  // Normaliser les valeurs pour la comparaison
  const value = fieldValue !== undefined && fieldValue !== null 
                ? String(fieldValue).trim() 
                : '';
                
  const compareValue = condition.value !== undefined && condition.value !== null 
                       ? String(condition.value).trim() 
                       : '';
  
  console.log(`Comparaison: '${value}' vs '${compareValue}'`);
  
  switch (condition.operator.toUpperCase()) {
    case 'EQUALS':
      return value === compareValue;
    case 'NOTEQUALS':
      return value !== compareValue;
    case 'CONTAINS':
      return value.includes(compareValue);
    case 'STARTSWITH':
      return value.startsWith(compareValue);
    case 'ENDSWITH':
      return value.endsWith(compareValue);
    case 'GREATERTHAN':
      return parseFloat(value) > parseFloat(compareValue);
    case 'LESSTHAN':
      return parseFloat(value) < parseFloat(compareValue);
    case 'NOTEMPTY':
      return value !== '';
    case 'ISEMPTY':
      return value === '';
    default:
      console.warn(`Opérateur inconnu: ${condition.operator}`);
      return false;
  }
}

export default fieldVisibilityService;